import { useEffect, useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { NAV_LINKS, ASSETS, BRAND } from '@/lib/brand';
import { openCalendly } from '@/lib/calendly';

const SECTION_MAP = {
  '#home': 'home',
  '#why-us': 'why-us',
  '#who-we-serve': 'who-we-serve',
  '#framework': 'framework',
  '#services': 'services',
  '#about': 'about',
  '#contact': 'contact',
};

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section tracking via IntersectionObserver
  useEffect(() => {
    const ids = Object.values(SECTION_MAP);
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!elements.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-nav py-3' : 'glass-nav-top py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); scrollTo('#home'); }}
          data-testid="nav-brand-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={ASSETS.monogramDark}
            alt="Bowers Advisory Group monogram"
            className="h-11 w-11 object-contain rounded-sm ring-1 ring-[#B68A35]/30 group-hover:ring-[#B68A35]/70 transition-all duration-500"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-white text-lg tracking-wide">Bowers</span>
            <span className="text-[#D6B36A] text-[0.62rem] tracking-[0.28em] uppercase mt-1">Advisory Group</span>
          </div>
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            const id = SECTION_MAP[l.href];
            const isActive = id === activeId;
            return (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|&/g, '-')}`}
                data-active={isActive}
                className={`relative text-[0.85rem] tracking-wide font-light transition-colors duration-300 ${
                  isActive ? 'text-[#D6B36A]' : 'text-white/80 hover:text-[#D6B36A]'
                }`}
              >
                {l.label}
                <span
                  className={`absolute left-0 right-0 -bottom-1.5 h-px bg-[#D6B36A] transition-transform duration-500 origin-left ${
                    isActive ? 'scale-x-100' : 'scale-x-0'
                  }`}
                />
              </a>
            );
          })}
        </nav>

        {/* CTA + Mobile trigger */}
        <div className="flex items-center gap-3">
          <a
            href={BRAND.phoneHref}
            data-testid="nav-phone-link"
            className="hidden md:flex items-center gap-2 text-white/75 hover:text-[#D6B36A] text-sm transition-colors duration-300"
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} />
            <span className="tracking-wide">{BRAND.phone}</span>
          </a>
          <button
            onClick={openCalendly}
            data-testid="nav-schedule-cta"
            className="hidden md:inline-flex btn-gold text-sm px-5 py-2.5"
          >
            Schedule Consultation
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2 hover:text-[#D6B36A] transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden mt-3 mx-4 rounded-sm border border-[#B68A35]/25 bg-[#070B12]/95 backdrop-blur-xl px-6 py-6 fade-in"
        >
          <div className="flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s|&/g, '-')}`}
                className="text-white/85 hover:text-[#D6B36A] tracking-wide text-base transition-colors py-1"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={openCalendly}
              data-testid="nav-mobile-schedule-cta"
              className="btn-gold mt-3 w-full"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
